import { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Drawer,
  Form,
  Image,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import type {
  ChannelProduct,
  PriceSnapshot,
  StandardProduct,
} from "@glow-price/shared";
import {
  EDITORIAL_TAGS,
  ENTITY_STATUSES,
  PRODUCT_CATEGORIES,
  formatPriceCNY,
} from "@glow-price/shared";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  auditLogsApi,
  brandsApi,
  channelProductsApi,
  priceSnapshotsApi,
  standardProductsApi,
  type AuditLog,
  type ImportSummary,
} from "../api/resources";
import { AliasesInput } from "../components/AliasesInput";
import { ImageUpload } from "../components/ImageUpload";
import { ImportResultModal } from "../components/ImportResultModal";
import { EmptyState, EnhancedTable } from "../components";

const CATEGORY_OPTIONS = Object.values(PRODUCT_CATEGORIES).map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));
const TAG_OPTIONS = EDITORIAL_TAGS.map((v) => ({ value: v, label: v }));

const TAG_COLOR: Record<string, string> = {
  明星单品: "#6d2839",
  新品: "#2e5d8a",
  经典款: "#4a3f33",
  热销: "#a87830",
  限定: "#4a1c2a",
};

type ProductFormValues = Partial<StandardProduct>;

export function StandardProductsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [q, setQ] = useState("");
  const [brandId, setBrandId] = useState<string | undefined>();
  const [tagFilter, setTagFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<StandardProduct | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [importResult, setImportResult] = useState<ImportSummary | null>(null);
  const [form] = Form.useForm<ProductFormValues>();

  const importMutation = useMutation({
    mutationFn: (file: File) => standardProductsApi.importCsv(file),
    onSuccess: (data) => {
      setImportResult(data);
      message.success(`导入完成 新增 ${data.inserted} / 更新 ${data.updated}`);
      qc.invalidateQueries({ queryKey: ["standard-products"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const brandsQuery = useQuery({
    queryKey: ["brands", "all"],
    queryFn: () => brandsApi.list({ page: 1, pageSize: 200 }),
  });
  const brandOptions = (brandsQuery.data?.items ?? []).map((b) => ({
    value: b.id,
    label: `${b.nameCn}${b.nameEn ? ` / ${b.nameEn}` : ""}`,
  }));

  const list = useQuery({
    queryKey: ["standard-products", q, brandId, tagFilter, page],
    queryFn: () =>
      standardProductsApi.list({ q, brandId, tag: tagFilter, page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<StandardProduct>) => {
      if (editing) return standardProductsApi.update(editing.id, values);
      return standardProductsApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["standard-products"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => standardProductsApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["standard-products"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  // Reset drawer form + tab when opening/closing.
  useEffect(() => {
    if (drawerOpen) {
      setActiveTab("overview");
      form.resetFields();
      form.setFieldsValue(
        (editing ?? {
          aliases: [],
          tags: [],
          status: ENTITY_STATUSES.ACTIVE,
          category: PRODUCT_CATEGORIES.SKINCARE,
        }) as ProductFormValues,
      );
    }
  }, [drawerOpen, editing, form]);

  const handleSave = async () => {
    const values = await form.validateFields();
    await saveMutation.mutateAsync(values);
  };

  const items = list.data?.items ?? [];
  const showEmpty = !list.isLoading && items.length === 0;

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          标准商品管理
        </Typography.Title>
        <Space>
          <Input.Search
            placeholder="搜索名称或别名"
            allowClear
            onSearch={(v) => {
              setPage(1);
              setQ(v);
            }}
            style={{ width: 240 }}
          />
          <Select
            allowClear
            placeholder="按品牌"
            options={brandOptions}
            value={brandId}
            onChange={(v) => {
              setPage(1);
              setBrandId(v);
            }}
            style={{ width: 200 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
          />
          <Select
            allowClear
            placeholder="按编辑标签"
            options={TAG_OPTIONS}
            value={tagFilter}
            onChange={(v) => {
              setPage(1);
              setTagFilter(v);
            }}
            style={{ width: 160 }}
          />
          <Upload
            accept=".csv"
            showUploadList={false}
            beforeUpload={(file) => {
              importMutation.mutate(file as File);
              return false;
            }}
          >
            <Button loading={importMutation.isPending}>导入 CSV</Button>
          </Upload>
          <Button
            type="primary"
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            新建商品
          </Button>
        </Space>
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
        CSV 列：<code>name, brand_code, category_slug, spec, image_url, barcode, aliases, description</code>
      </Typography.Paragraph>

      {showEmpty ? (
        <EmptyState
          title="暂无标准商品"
          description="尚未创建任何商品。点击「新建商品」开始添加，或通过 CSV 批量导入。"
          action={
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              新建商品
            </Button>
          }
        />
      ) : (
        <EnhancedTable
          rowKey={(r) => r.product.id}
          loading={list.isLoading}
          dataSource={items}
          persistKey="standard-products"
          exportFilename="standard-products"
          pagination={{
            current: page,
            pageSize: 20,
            total: list.data?.total ?? 0,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
          }}
          columns={[
            { title: "商品名", key: "name", render: (_, row) => row.product.name },
            { title: "品牌", key: "brand", render: (_, row) => row.brand.nameCn, width: 120 },
            { title: "分类", key: "category", render: (_, row) => row.product.category, width: 90 },
            { title: "规格", key: "spec", render: (_, row) => row.product.spec ?? "—", width: 80 },
            {
              title: "编辑标签",
              key: "tags",
              width: 200,
              render: (_, row) => {
                const tags = row.product.tags ?? [];
                if (tags.length === 0) return <Typography.Text type="secondary">—</Typography.Text>;
                return (
                  <Space size={4} wrap>
                    {tags.map((t) => (
                      <Tag key={t} color={TAG_COLOR[t] ?? "default"}>
                        {t}
                      </Tag>
                    ))}
                  </Space>
                );
              },
            },
            { title: "渠道", key: "channelCount", render: (_, row) => row.channelCount, width: 60 },
            {
              title: "最低",
              key: "lowest",
              render: (_, row) => formatPriceCNY(row.lowestFinalPrice),
              width: 100,
            },
            { title: "状态", key: "status", render: (_, row) => row.product.status, width: 80 },
            {
              title: "操作",
              key: "actions",
              width: 140,
              render: (_, row) => (
                <Space>
                  <Button
                    type="link"
                    onClick={() => {
                      setEditing(row.product);
                      setDrawerOpen(true);
                    }}
                  >
                    编辑
                  </Button>
                  <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.product.id)}>
                    <Button type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      )}

      <Drawer
        open={drawerOpen}
        title={editing ? `编辑商品 - ${editing.name}` : "新建标准商品"}
        width={640}
        destroyOnClose
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        extra={
          <Space>
            <Button
              onClick={() => {
                setDrawerOpen(false);
                setEditing(null);
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={saveMutation.isPending}
              onClick={handleSave}
            >
              保存
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "overview",
              label: "概览",
              children: (
                <Form layout="vertical" form={form}>
                  <Form.Item name="brandId" label="品牌" rules={[{ required: true }]}>
                    <Select
                      options={brandOptions}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                  <Form.Item name="name" label="商品名" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="category" label="分类" rules={[{ required: true }]}>
                    <Select options={CATEGORY_OPTIONS} />
                  </Form.Item>
                  <Form.Item name="spec" label="规格">
                    <Input placeholder="100ml / 60g" />
                  </Form.Item>
                  <Form.Item
                    name="tags"
                    label="编辑标签"
                    tooltip="决定首页编辑位归属（明星单品 / 新品 / 经典款 / 限定）+ 列表页 '热销' 徽章"
                  >
                    <Select
                      mode="multiple"
                      options={TAG_OPTIONS}
                      placeholder="选择 0 个或多个"
                      allowClear
                    />
                  </Form.Item>
                  <Form.Item name="imageUrl" label="商品图">
                    <ImageUpload folder="standard-products" />
                  </Form.Item>
                  <Form.Item
                    shouldUpdate={(prev, cur) => prev.imageUrl !== cur.imageUrl}
                    noStyle
                  >
                    {({ getFieldValue }) => {
                      const url = getFieldValue("imageUrl") as string | undefined;
                      if (!url) return null;
                      return (
                        <Form.Item label="图片预览">
                          <Image
                            src={url}
                            alt="预览"
                            width={160}
                            height={160}
                            style={{ objectFit: "cover", borderRadius: 8, border: "1px solid #e8e2dc" }}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                  <Form.Item name="barcode" label="条码">
                    <Input />
                  </Form.Item>
                  <Form.Item name="aliases" label="别名">
                    <AliasesInput />
                  </Form.Item>
                  <Form.Item
                    name="description"
                    label="描述"
                    extra="使用 Markdown 语法：**加粗** / *斜体* / 链接 [text](url) / 列表 - item。"
                  >
                    <Input.TextArea
                      rows={5}
                      placeholder={"例如：\n**产品亮点**\n- 香根草调底香\n- 限定瓶身"}
                      style={{ fontFamily: "monospace", fontSize: 12 }}
                    />
                  </Form.Item>
                  <Form.Item name="status" label="状态">
                    <Select options={STATUS_OPTIONS} />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: "channels",
              label: "渠道商品",
              disabled: !editing,
              children: editing ? <ChannelProductsTab productId={editing.id} /> : null,
            },
            {
              key: "history",
              label: "价格历史",
              disabled: !editing,
              children: editing ? <PriceHistoryTab productId={editing.id} /> : null,
            },
            {
              key: "audit",
              label: "审计日志",
              disabled: !editing,
              children: editing ? <AuditLogTab productId={editing.id} /> : null,
            },
          ]}
        />
      </Drawer>
      <ImportResultModal result={importResult} onClose={() => setImportResult(null)} />
    </div>
  );
}

// ─── 渠道商品 tab ──────────────────────────────────────────────────────────
function ChannelProductsTab({ productId }: { productId: string }) {
  const cpQuery = useQuery({
    queryKey: ["channel-products", "by-standard", productId],
    queryFn: () =>
      channelProductsApi.list({ standardProductId: productId, pageSize: 100 }),
  });

  const items = cpQuery.data?.items ?? [];
  if (cpQuery.isLoading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }
  if (items.length === 0) {
    return (
      <EmptyState
        title="暂无渠道商品"
        description="此商品尚未在任何渠道上架。请前往「渠道商品」页面绑定。"
      />
    );
  }
  return (
    <Table<ChannelProduct>
      rowKey="id"
      size="small"
      pagination={false}
      dataSource={items}
      columns={[
        { title: "渠道 ID", dataIndex: "channelId", width: 200, ellipsis: true },
        { title: "渠道商品 ID", dataIndex: "channelProductId", ellipsis: true },
        { title: "渠道商品名", dataIndex: "channelProductName", ellipsis: true },
        { title: "状态", dataIndex: "status", width: 90 },
      ]}
    />
  );
}

// ─── 价格历史 tab ─────────────────────────────────────────────────────────
function PriceHistoryTab({ productId }: { productId: string }) {
  // We need channel_products first to pull their snapshots. priceSnapshotsApi
  // accepts a single channelProductId at a time today, so we fetch the first
  // channel product's snapshots as a representative trend. A multi-CP trend
  // would need a new backend endpoint.
  // TODO(api): expose /api/admin/price-snapshots?standardProductId=… so the
  // dashboard can render an aggregate across channels.
  const cpQuery = useQuery({
    queryKey: ["channel-products", "by-standard", productId],
    queryFn: () =>
      channelProductsApi.list({ standardProductId: productId, pageSize: 1 }),
  });

  const firstCpId = cpQuery.data?.items?.[0]?.id ?? null;
  const snapsQuery = useQuery({
    queryKey: ["price-snapshots", "by-cp", firstCpId],
    enabled: !!firstCpId,
    queryFn: () =>
      priceSnapshotsApi.list({ channelProductId: firstCpId!, pageSize: 90 }),
  });

  const data = useMemo(() => {
    const items = snapsQuery.data?.items ?? [];
    // sort ascending by capturedAt so the line goes left→right
    return [...items]
      .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
      .map((s: PriceSnapshot) => ({
        day: dayjs(s.capturedAt).format("MM-DD"),
        price: s.finalPrice,
      }));
  }, [snapsQuery.data]);

  if (cpQuery.isLoading || snapsQuery.isLoading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }
  if (!firstCpId) {
    return <EmptyState title="暂无价格历史" description="此商品尚未在任何渠道上架。" />;
  }
  if (data.length === 0) {
    return <EmptyState title="暂无价格历史" description="此渠道商品尚无价格快照。" />;
  }

  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        显示首个渠道商品的最近 90 条快照。TODO(api)：多渠道聚合接口待支持。
      </Typography.Paragraph>
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#e8e2dc" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: "#8a8580" }}
              axisLine={{ stroke: "#e8e2dc" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#8a8580" }}
              axisLine={false}
              tickLine={false}
              width={50}
            />
            <RTooltip />
            <Line
              type="monotone"
              dataKey="price"
              stroke="#6d2839"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── 审计日志 tab ─────────────────────────────────────────────────────────
function AuditLogTab({ productId }: { productId: string }) {
  const logsQuery = useQuery({
    queryKey: ["audit-logs", "by-entity", productId],
    queryFn: () =>
      auditLogsApi.list({
        entityType: "standard_product",
        entityId: productId,
        pageSize: 30,
      }),
  });

  const items = logsQuery.data?.items ?? [];
  if (logsQuery.isLoading) {
    return <Typography.Text type="secondary">加载中…</Typography.Text>;
  }
  if (items.length === 0) {
    return <EmptyState title="暂无审计日志" description="此商品尚无任何操作记录。" />;
  }
  return (
    <Table<AuditLog>
      rowKey="id"
      size="small"
      pagination={{ pageSize: 10, showSizeChanger: false }}
      dataSource={items}
      columns={[
        {
          title: "时间",
          dataIndex: "createdAt",
          width: 140,
          render: (v: string) => dayjs(v).format("MM-DD HH:mm"),
        },
        { title: "操作人", dataIndex: "adminUsername", width: 110 },
        { title: "动作", dataIndex: "action" },
      ]}
    />
  );
}
